import { Alert, flash, SubmitButton } from "@xola/ui-kit";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { api } from "../../../api/api";
import { config } from "../../../config";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { isStripeConnectOAuth } from "../../../models/Seller";
import { ContentLayout } from "../../Layout/ContentLayout";

enum AuthorizationStatus {
    /** Seller has stripe payment gateway, but connect account is not set-up */
    DEFAULT = "default",

    /** Seller is trying to authorize the connect account */
    AUTHORIZING = "authorizing",

    /** Seller has successfully set up the connect account */
    AUTHORIZED = "authorized",

    /** Seller is trying to verify the credentials using "Verify" button. The API call is in progress */
    VERIFYING = "verifying",

    /** The `/verify` API has returned success response. */
    VERIFIED = "verified",

    /** The `/verify` API failed. Seller needs to reauthorize the credentials */
    VERIFICATION_FAILED = "verification-failed",

    /** Seller is re-authorizing the connect account */
    REAUTHORIZING = "reauthorizing",
}

const StripeOAuthGateway = () => {
    return (
        <ContentLayout.Section title="Stripe OAuth">
            <StripeOAuthGatewayContent />
        </ContentLayout.Section>
    );
};

const StripeOAuthGatewayContent = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();

    const stripeWindow = useRef<Window | null>();
    const stripeWindowInterval = useRef<NodeJS.Timer | null>();
    const [status, setStatus] = useState<AuthorizationStatus>(AuthorizationStatus.DEFAULT);

    const { mutateAsync: verifyStripeGateway } = useMutation(async () => {
        if (!seller.defaultGateway?.id) return;

        return api.get(`${config.xola.url}/api/users/${seller.id}/remoteGateways/${seller.defaultGateway.id}/verify`, {
            suppressErrorToast: true,
        });
    });

    const clearStripeWindowInterval = () => {
        if (stripeWindowInterval.current) {
            clearInterval(stripeWindowInterval.current);
        }

        stripeWindow.current = null;
    };

    const handleAuthorize = async () => handleConnect(AuthorizationStatus.AUTHORIZING);

    const handleReauthorize = async () => handleConnect(AuthorizationStatus.REAUTHORIZING);

    const handleConnect = async (source: AuthorizationStatus.AUTHORIZING | AuthorizationStatus.REAUTHORIZING) => {
        setStatus(source);

        const popup = openStripeAuthorizationWindow();
        stripeWindow.current = popup;

        try {
            const { data } = await api.get<{ authorizationUrl: string }>("/oauth/authorize", {
                params: { provider: "stripe", user: seller.id, seller: seller.id },
                headers: { Accept: "application/json" },
            });
            if (popup) {
                popup.location = data.authorizationUrl;
            }
        } catch {
            popup?.close();
            handleStripeWindowClosed(source);
            return;
        }

        /** Set an interval to check if stripe window is closed or not  */
        stripeWindowInterval.current = setInterval(() => {
            if (stripeWindow.current?.closed) {
                handleStripeWindowClosed(source);
            }
        }, 500);
    };

    const handleStripeWindowClosed = (source: AuthorizationStatus.AUTHORIZING | AuthorizationStatus.REAUTHORIZING) => {
        clearStripeWindowInterval();

        invalidate("sellers", seller.id);

        /** We set back the authorization state to the last. If the credentials are updated successfully when stripe window is closed, the `useEffect` would change the state accordingly once the sellers API responds  */
        setStatus(
            source === AuthorizationStatus.AUTHORIZING
                ? AuthorizationStatus.DEFAULT
                : AuthorizationStatus.VERIFICATION_FAILED,
        );
    };

    const handleVerify = async () => {
        if (!seller.defaultGateway?.id) return;

        setStatus(AuthorizationStatus.VERIFYING);

        try {
            await verifyStripeGateway();
            setStatus(AuthorizationStatus.VERIFIED);
        } catch (error: any) {
            if (axios.isAxiosError(error) && error.response?.status === 403) {
                setStatus(AuthorizationStatus.VERIFICATION_FAILED);
                return;
            }

            flash.danger("Connection to gateway failed, please try after few minutes");
            setStatus(AuthorizationStatus.DEFAULT);
        }
    };

    useEffect(() => {
        setStatus(
            isStripeConnectOAuth(seller.defaultGateway) ? AuthorizationStatus.AUTHORIZED : AuthorizationStatus.DEFAULT,
        );
    }, [seller.defaultGateway]);

    useEffect(() => {
        return () => {
            /** Cleanup any intervals when component is destroyed */
            clearStripeWindowInterval();
        };
    }, []);

    if (status === AuthorizationStatus.DEFAULT || status === AuthorizationStatus.AUTHORIZING) {
        return (
            <SubmitButton isLoading={status === AuthorizationStatus.AUTHORIZING} onClick={handleAuthorize}>
                Connect with Stripe
            </SubmitButton>
        );
    }

    if (status === AuthorizationStatus.VERIFIED) {
        return (
            <Alert color="success">
                <strong>Verification Successful.</strong> Your stripe credentials are valid
            </Alert>
        );
    }

    if (status === AuthorizationStatus.AUTHORIZED || status === AuthorizationStatus.VERIFYING) {
        return (
            <Alert>
                <div className="flex items-center justify-between">
                    <span>Your account is connected to Stripe.</span>
                    <SubmitButton
                        size="small"
                        isLoading={status === AuthorizationStatus.VERIFYING}
                        onClick={handleVerify}
                    >
                        Verify
                    </SubmitButton>
                </div>
            </Alert>
        );
    }

    /** status === AuthorizationStatus.VERIFICATION_FAILED || status === AuthorizationStatus.REAUTHORIZING */
    return (
        <Alert color="danger">
            <div className="flex items-center justify-between">
                <span>Verification failed. Your credentials are invalid</span>
                <SubmitButton
                    color="danger"
                    size="small"
                    isLoading={status === AuthorizationStatus.REAUTHORIZING}
                    onClick={handleReauthorize}
                >
                    Connect with Stripe
                </SubmitButton>
            </div>
        </Alert>
    );
};

const openStripeAuthorizationWindow = () => {
    return window.open(
        undefined,
        "stripe-connect",
        "scrollbars=yes,resizable=yes,status=no,toolbar=no,menubar=no,width=1024,height=700,top=100,left=100",
    );
};

export default StripeOAuthGateway;
